import { defineStore, acceptHMRUpdate } from 'pinia'
import { tnApi } from '@/api'
import { useNotification } from '@/composables/useNotification.js'
import { useDashboardStore } from '@/pinia/DashboardStore'
import { useInquiryStore } from '@/pinia/InquiryStore.js'
import { pick } from 'lodash'
import * as Sentry from '@sentry/vue'

export const useGlobalStore = defineStore('global', {
  state: () => ({
    requestCount: 0,
    errors: [],
    params: {},
    backRoute: null,
    user: null,
    admin: null,
    token: null,
    legacyToken: null,
    defaultEntity: 0,
    newCD: null,
    notifications: [],
    axiosError: null,
    selectedItems: [],
    aaul: false,
    signature: ''
  }),
  getters: {
    isLoggedIn: (state) => {
      return (state.getToken() !== null)
    },
    hasUnreadNotifications: (state) => state.unReadNotifications.length > 0,
    unReadNotifications: (state) => {
      const unread = state.notifications.filter(item => item.unread)
      return unread
    }
  },
  actions: {
    async loadParams (types) {
      const { loadParams } = tnApi()
      const result = await loadParams(types)

      const keys = Object.keys(result.params)
      for (const key of keys) {
        this.params[key] = result.params[key]
      }
    },
    async BridgeLegacyRedirect () {
      const {
        obuBridgeLegacyRedirect
      } = tnApi(true)
      const { token } = await obuBridgeLegacyRedirect()
      return token
    },
    async AuthResetPassword (email) {
      const {
        AuthReset
      } = tnApi(false)
      try {
        await AuthReset(email)
      } catch (error) {
        return Promise.recject(error)
      }
    },
    async updateUserProfile (payload) {
      const {
        updateUserProfile
      } = tnApi(true)
      const { user } = await updateUserProfile(payload)
      sessionStorage.setItem('user', JSON.stringify(user))
      const store = useGlobalStore()
      store.$patch((state) => {
        state.user = user
      })
    },
    async changePassword (payload) {
      const {
        changePassword
      } = tnApi(true)

      try {
        await changePassword(payload)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async SignIn (payload) {
      const {
        AuthSignIn
      } = tnApi(false)
      try {
        const { user, token, aaul, defaultEntity, stats, smtp } = await AuthSignIn(payload)
        this.setUser(user, token, aaul, defaultEntity, stats, smtp)
        Sentry.setUser({ id: user.id })
        Sentry.setTag('login-type', 'user')
        Sentry.setTag('user', user.id)
        const dashboardStore = useDashboardStore()
        dashboardStore.resetStats()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async AaulSignIn (payload) {
      const {
        AuthAaul
      } = tnApi(false)
      try {
        const dashboardStore = useDashboardStore()
        dashboardStore.resetStats()
        const { user, token, aaul, defaultEntity, stats, smtp, admin } = await AuthAaul(payload)
        Sentry.setUser({ id: admin.id, email: admin.email })
        Sentry.setTag('admin', admin.id)
        Sentry.setTag('user', user.id)
        Sentry.setTag('login-type', 'aaul')
        this.setUser(user, token, aaul, defaultEntity, stats, smtp)
        this.setAdmin(aaul, pick(admin, ['id', 'r_full_name', 'email', 'first_name']))
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async BridgeSignIn (payload) {
      const {
        AuthBridge
      } = tnApi(false)
      try {
        const dashboardStore = useDashboardStore()
        dashboardStore.resetStats()
        const { user, token, aaul, defaultEntity, stats } = await AuthBridge(payload)
        this.setUser(user, token, aaul, defaultEntity, stats)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async SignOut (payload) {
      const {
        AuthSignOut
      } = tnApi(false)
      try {
        // await AuthSignOut()
        const dashboardStore = useDashboardStore()
        dashboardStore.resetStats()
        sessionStorage.clear()
        localStorage.clear()
        this.user = null
        this.token = null
        this.aaul = false
        this.defaultEntity = 0
      } catch (error) {
        return Promise.reject(error)
      }
    },
    setSmtp (smpt) {
      this.user.smtp = smpt
      sessionStorage.setItem('user', JSON.stringify(this.user))
    },
    setAdmin (aaul, admin) {
      if (aaul) {
        sessionStorage.setItem('admin', JSON.stringify(admin))
        this.admin = admin
      } else {
        sessionStorage.removeItem('admin')
        this.admin = null
      }
    },
    setUser (user, token, aaul, defaultEntity, stats) {
      sessionStorage.setItem('token', token.token)
      sessionStorage.setItem('user', JSON.stringify(user))
      sessionStorage.setItem('aaul', JSON.stringify(aaul))
      sessionStorage.setItem('defaultEntity', defaultEntity)

      const inquiryStore = useInquiryStore()
      inquiryStore.setStats(stats.inquiries)

      this.defaultEntity = defaultEntity
      this.user = user
      this.token = token.token
      this.aaul = aaul
    },
    getToken () {
      return sessionStorage.getItem('token')
    },
    refreshFromStorage () {
      this.user = JSON.parse(sessionStorage.getItem('user'))
      this.aaul = JSON.parse(sessionStorage.getItem('aaul'))
      this.admin = JSON.parse(sessionStorage.getItem('admin'))
    },
    getDefaultEntity () {
      return Number(sessionStorage.getItem('defaultEntity'))
    },
    resetRequestCounter () {
      this.requestCount = 0
    },
    async getSignature () {
      const { loadSignature } = tnApi()
      const { signature } = await loadSignature()
      this.signature = signature
    },
    async saveSignature () {
      const { saveSignature } = tnApi()
      await saveSignature(this.signature)
    },
    async getNotifications () {
      const { loadNotifications } = tnApi()
      const { notifications } = await loadNotifications()

      const store = useGlobalStore()
      store.$patch((state) => {
        state.notifications = notifications
        state.hasChanged = true
      })

      sessionStorage.setItem('notification', this.notifications)
    },
    async setNotificationsRead (id) {
      const {
        notificationSetRead,
        notificationSetReadAll
      } = tnApi()
      if (!id) {
        await notificationSetReadAll()
      } else {
        await notificationSetRead(id)
      }
      await this.getNotifications()
    },
    clearNotificationCounter () {
      // this.unReadNotifications = 0
    },
    setNotifications (notification) {
      if (!this.notifications?.length) {
        const sessionNotifications = sessionStorage.getItem('notifications')
        this.notifications = sessionNotifications ? JSON.parse(sessionNotifications) : []
      }

      const { sendNoticeByPayload, sendBookingToast, sendDocumentToast, sendNotice } = useNotification()
      this.unReadNotifications = notification.length
      const existingNotificationIndex = this.notifications.findIndex(item => item.data.uuid === notification.data.uuid)
      if (existingNotificationIndex === -1) {
        this.notifications.push(notification)

        if (notification.unread) {
          if (notification.type === 'booking.toast') {
            sendBookingToast(notification.data.booking)
          }
        }
      } else {
        this.notifications[existingNotificationIndex] = notification
        if (notification.unread) {
          sendNoticeByPayload(notification.data)
        }
      }

      const store = useGlobalStore()
      store.$patch((state) => {
        state.notifications = this.notifications
        state.hasChanged = true
      })

      sessionStorage.setItem('notification', this.notifications)
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useGlobalStore, import.meta.hot))
}
